import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Dashboard,
  Description,
  CheckCircle,
  Payments,
  Business,
  AttachFile,
} from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PremiumLanguageSelector from "../components/common/LanguageSwitcher";
import LanguageIcon from "@mui/icons-material/Language";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppTranslation } from "../hooks/useAppTranslation";

import logo from "../assets/eko.png";

interface SidebarProps {
  drawerWidth: number;
  collapsed: boolean;
}

export default function Sidebar({ drawerWidth, collapsed }: SidebarProps) {
  const { i18n } = useTranslation();
  const { t } = useAppTranslation("common");
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const handleLanguageChange = async (lang: string) => {
    await i18n.changeLanguage(lang);
    localStorage.setItem("appLanguage", lang);
    setLangAnchor(null);
  };

  const { user } = useAuth();

  const baseMenuItems = [
    { text: t("Panel główny"), icon: <Dashboard />, path: "/dashboard" },
    { text: t("Wszystkie faktury"), icon: <Description />, path: "/documents" },
    { text: t("Do akceptacji"), icon: <CheckCircle />, path: "/documents/pending" },
    { text: t("Płatności"), icon: <Payments />, path: "/payments" },
    { text: t("Dostawcy"), icon: <Business />, path: "/vendors" },
    { text: t("Dokumenty inne"), icon: <AttachFile />, path: "/additional-documents" },
  ];

  const showAdminPanel = user?.role === "admin";

  const adminMenuItem = showAdminPanel
    ? {
        text: t("Panel administratora"),
        icon: <AdminPanelSettingsIcon />,
        path: "/admin",
      }
    : null;

  const menuItems = adminMenuItem ? [...baseMenuItems, adminMenuItem] : baseMenuItems;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s ease",
          overflowX: "hidden",

          background: "linear-gradient(180deg, #1F3A4D 0%, #162E3D 100%)",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      {/* LOGO */}
      <Box
        component={NavLink}
        to="/dashboard"
        sx={{
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          textDecoration: "none",
        }}
      >
        {collapsed ? (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#E5EDF2",
              fontWeight: 700,
              letterSpacing: 0.6,
            }}
          >
            EKO
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: 2,
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                height: 32,
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Box>

      <Toolbar />

      {/* MAIN MENU */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List sx={{ px: collapsed ? 0.5 : 0 }}>
          {menuItems.map((item) => (
            <Tooltip
              key={item.text}
              title={collapsed ? item.text : ""}
              placement="right"
              arrow
            >
              <ListItemButton
                component={NavLink}
                to={item.path}
                end={item.path === "/documents"}
                sx={{
                  mx: 1.5,
                  mb: 0.5,
                  borderRadius: 2,
                  color: "rgba(229, 237, 242, 0.85)",
                  transition: "all 0.2s ease",
                  justifyContent: collapsed ? "center" : "flex-start",
                  px: collapsed ? 1.25 : 2,

                  "& .MuiListItemIcon-root": {
                    color: "inherit",
                    minWidth: 0,
                    mr: collapsed ? 0 : 1.5,
                    justifyContent: "center",
                  },

                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                    transform: collapsed ? "none" : "translateX(2px)",
                  },

                  "&.active": {
                    backgroundColor: "rgba(28, 110, 140, 0.45)",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    boxShadow: "inset 3px 0 0 #4FC3F7",
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          px: collapsed ? 1.5 : 2.5,
          pb: 2.5,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2 }} />
      {!collapsed ? (
        <PremiumLanguageSelector />
      ) : (
        <>
          <IconButton
            onClick={(e) => setLangAnchor(e.currentTarget)}
            sx={{
              width: "100%",
              color: "rgba(229, 237, 242, 0.7)",
            }}
          >
            <LanguageIcon />
          </IconButton>

          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: 2,
              },
            }}
          >
            <MenuItem onClick={() => handleLanguageChange("pl")}>
              Polski
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange("en")}>
              English
            </MenuItem>
          </Menu>
        </>
      )}
      </Box>
    </Drawer>
  );
}